import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Mail, Send, Users, Clock, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x.toISOString().split('T')[0];
};

// Default = next 7 days starting today
const defaultStart = () => new Date().toISOString().split('T')[0];
const defaultEnd = () => addDays(new Date(), 6);

export const EmailScheduleModal = ({ open, onClose }) => {
  const [startDate, setStartDate] = useState(defaultStart());
  const [endDate, setEndDate] = useState(defaultEnd());
  const [recipientFilter, setRecipientFilter] = useState('all_active');
  const [includeUnassigned, setIncludeUnassigned] = useState(false);
  const [message, setMessage] = useState('');
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const fetchPreview = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoadingPreview(true);
    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        recipient_filter: recipientFilter,
        include_unassigned: String(includeUnassigned),
      });
      const res = await axios.get(`${API_URL}/api/admin/schedule/email/preview?${params}`, { withCredentials: true });
      setPreview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load preview');
    } finally {
      setLoadingPreview(false);
    }
  }, [startDate, endDate, recipientFilter, includeUnassigned]);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    fetchPreview();
  }, [open, fetchPreview]);

  const send = async () => {
    if (!preview || preview.total_will_receive_email === 0) {
      toast.error('No employees have shifts in this range');
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/schedule/email`, {
        start_date: startDate,
        end_date: endDate,
        message,
        recipient_filter: recipientFilter,
        include_unassigned: includeUnassigned,
      }, { withCredentials: true });
      setResult(res.data);
      toast.success(`Sent to ${res.data.sent} employee(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="email-schedule-modal">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Fredoka' }} className="flex items-center gap-2 text-[#FF3B6B]">
            <Mail className="w-5 h-5" /> Email Shift Schedule
          </DialogTitle>
        </DialogHeader>

        {result ? (
          <div className="space-y-4" data-testid="email-schedule-result">
            <div className="p-4 rounded-xl bg-[#06D6A0]/10 border border-[#06D6A0]/30 flex gap-3">
              <CheckCircle2 className="w-6 h-6 text-[#06D6A0] flex-shrink-0" />
              <div>
                <p className="font-semibold text-[#06D6A0]">Emails sent!</p>
                <p className="text-sm">{result.sent} sent · {result.skipped_no_shifts} skipped (no shifts) · {result.failed} failed</p>
              </div>
            </div>
            {result.details?.length > 0 && (
              <div className="bg-[#FFFDF8] border border-[#E8E5DF] rounded-xl p-3 max-h-60 overflow-y-auto text-xs font-mono">
                {result.details.map((d, i) => (
                  <div key={i} className={`flex justify-between py-0.5 ${d.status === 'failed' ? 'text-red-600' : d.status === 'skipped_no_shifts' ? 'text-[#6B6B7A]' : 'text-[#1E1E24]'}`}>
                    <span>{d.email}</span>
                    <span>{d.status === 'sent' ? `✓ ${d.shift_count} shifts (${d.total_hours}h)` : d.status}</span>
                  </div>
                ))}
              </div>
            )}
            <Button onClick={onClose} className="w-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="schedule-email-close-btn">Close</Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Date range */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-semibold">From *</Label>
                <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="sched-start-date" />
              </div>
              <div>
                <Label className="font-semibold">To *</Label>
                <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="sched-end-date" />
              </div>
            </div>

            <div className="flex gap-2 text-xs">
              <Button size="sm" variant="outline" onClick={() => { setStartDate(defaultStart()); setEndDate(defaultEnd()); }} className="rounded-full border-[#E8E5DF]" data-testid="preset-next7">Next 7 days</Button>
              <Button size="sm" variant="outline" onClick={() => { setStartDate(addDays(new Date(), 7)); setEndDate(addDays(new Date(), 13)); }} className="rounded-full border-[#E8E5DF]" data-testid="preset-nextweek">Next week</Button>
              <Button size="sm" variant="outline" onClick={() => { setStartDate(addDays(new Date(), 0)); setEndDate(addDays(new Date(), 30)); }} className="rounded-full border-[#E8E5DF]" data-testid="preset-month">Next 30 days</Button>
            </div>

            {/* Recipient filter */}
            <div>
              <Label className="font-semibold">Recipients</Label>
              <Select value={recipientFilter} onValueChange={setRecipientFilter}>
                <SelectTrigger className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="sched-recipient-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_active">All active employees</SelectItem>
                  <SelectItem value="party_host">Party Hosts only</SelectItem>
                  <SelectItem value="manager">Managers only</SelectItem>
                  <SelectItem value="staff">Staff only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Include unassigned */}
            <label className="flex items-center justify-between p-3 bg-[#FFFDF8] rounded-xl border border-[#E8E5DF] cursor-pointer">
              <div>
                <span className="font-semibold text-[#1E1E24] text-sm">Include unassigned shifts</span>
                <p className="text-xs text-[#6B6B7A]">Email an admin summary of shifts assigned to inactive/deleted employees</p>
              </div>
              <input
                type="checkbox"
                checked={includeUnassigned}
                onChange={e => setIncludeUnassigned(e.target.checked)}
                data-testid="sched-include-unassigned"
              />
            </label>

            {/* Personal message */}
            <div>
              <Label className="font-semibold">Personal message (optional)</Label>
              <Textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                rows={3}
                placeholder="e.g., Please confirm your shifts by Friday. Let me know if any conflicts!"
                className="mt-1 rounded-xl border-[#E8E5DF]"
                data-testid="sched-message"
              />
            </div>

            {/* Preview */}
            <div className="bg-[#FFFDF8] rounded-xl border border-[#E8E5DF] p-4" data-testid="sched-preview">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#FF3B6B]" />
                  <span className="font-semibold text-sm">Preview</span>
                </div>
                {loadingPreview && <span className="text-xs text-[#6B6B7A]">Loading…</span>}
              </div>
              {preview ? (
                <>
                  <p className="text-xs text-[#6B6B7A] mb-3">
                    {preview.total_employees_matched} employee(s) matched ·{' '}
                    <strong className="text-[#06D6A0]">{preview.total_will_receive_email}</strong> will receive email ·{' '}
                    {preview.total_shifts} total shifts in range
                  </p>
                  <div className="max-h-48 overflow-y-auto border border-[#E8E5DF] rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="bg-[#FFF4E6] sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">Employee</th>
                          <th className="px-3 py-2 text-left">Shifts</th>
                          <th className="px-3 py-2 text-left">Hours</th>
                          <th className="px-3 py-2 text-left">First shifts</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.per_employee.length === 0 ? (
                          <tr><td colSpan={4} className="text-center py-4 text-[#6B6B7A]">No employees match filter</td></tr>
                        ) : preview.per_employee.map(p => (
                          <tr key={p.user_id} className={`border-t border-[#E8E5DF] ${p.shift_count === 0 ? 'opacity-50' : ''}`}>
                            <td className="px-3 py-2">
                              <p className="font-medium">{p.name}</p>
                              <p className="text-[10px] text-[#6B6B7A]">{p.email}</p>
                            </td>
                            <td className="px-3 py-2 font-semibold">{p.shift_count}</td>
                            <td className="px-3 py-2">{p.total_hours}h</td>
                            <td className="px-3 py-2 text-[11px] text-[#6B6B7A]">
                              {p.shifts.map(s => `${s.date} ${s.start_time}`).join(' · ') || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {includeUnassigned && preview.unassigned_shifts?.length > 0 && (
                    <div className="mt-2 p-2 rounded bg-yellow-50 border border-yellow-200 text-xs text-yellow-800 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      {preview.unassigned_shifts.length} unassigned shift(s) will be summarized to admin
                    </div>
                  )}
                </>
              ) : (
                <p className="text-xs text-[#6B6B7A]">No data</p>
              )}
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} className="flex-1 rounded-full border-[#E8E5DF]" data-testid="sched-cancel">
                Cancel
              </Button>
              <Button
                onClick={send}
                disabled={sending || !preview || preview.total_will_receive_email === 0}
                className="flex-1 bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full"
                data-testid="send-schedule-btn"
              >
                {sending ? (
                  <span className="flex items-center"><Clock className="w-4 h-4 mr-1 animate-spin" /> Sending…</span>
                ) : (
                  <span className="flex items-center"><Send className="w-4 h-4 mr-1" /> Send to {preview?.total_will_receive_email || 0} employee(s)</span>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EmailScheduleModal;
